import { useEffect, useState, type ComponentPropsWithoutRef } from 'react';
import { LogoMark, TextLogo } from '../assets/assets';
import { cn } from '../lib/utils';

export type SolinkLogoVariant = 'wordmark' | 'mark';

export interface SolinkLogoProps extends Omit<ComponentPropsWithoutRef<'img'>, 'src'> {
  /** Full wordmark (default) or the collapsed “O” mark. */
  variant?: SolinkLogoVariant;
}

const VARIANT_SRC = {
  wordmark: TextLogo,
  mark: LogoMark,
} as const;

/** Figma nav `solink` — expanded wordmark 120×28 (`7045:2271`), collapsed mark 28×28 (`8615:17548`). */
const VARIANT_SIZE = {
  wordmark: 'h-7 w-[120px] object-contain',
  mark: 'size-7 object-contain',
} as const;

function readDocumentDarkClass() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

/** Follows the app / Storybook contract: theme is the `.dark` class on `<html>`. */
function useDocumentDarkClass() {
  const [isDark, setIsDark] = useState(readDocumentDarkClass);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

/**
 * Themed Solink logo. `variant="wordmark"` is the full lockup; `variant="mark"` is
 * the 28px “O” used on the collapsed rail. Theme follows `html.dark`.
 */
export function SolinkLogo({
  variant = 'wordmark',
  alt = 'Solink',
  className,
  ...props
}: SolinkLogoProps) {
  const isDark = useDocumentDarkClass();
  const srcs = VARIANT_SRC[variant];

  return (
    <img
      data-slot="solink-logo"
      data-variant={variant}
      src={isDark ? srcs.dark : srcs.light}
      alt={alt}
      className={cn(VARIANT_SIZE[variant], className)}
      {...props}
    />
  );
}
